import React, { useEffect, useState } from 'react';
import { IconArrowNarrowRight, IconPlus, IconSearch } from '@tabler/icons';
import { BringValueInput } from 'qubCommon/Inputs';
import MainCard from 'ui-component/cards/MainCard';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, ListItem, Button, Box, Paper, Badge, Chip } from '@mui/material';
import { format } from 'date-fns';
import { person, backoffice } from 'apis';
import { isNil } from 'lodash';
import usePerson from '../Users/hooks/usePerson';
import PersonInfo from 'ui-component/person/PersonInfo';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useQuery } from 'react-query';
import useDateTranslatorHelper from 'utils/date-translate';
import { useTheme } from '@emotion/react';
import {
    BadgeOutlined,
    EmojiEvents,
    Event,
    EventAvailable,
    LocationCity,
    LocationCityOutlined,
    Numbers,
    NumbersOutlined,
    PersonOutlineOutlined
} from '@mui/icons-material';
import ExplorerCard from 'ui-component/cards/ExplorerCard';

const PersonFunction = ({ f, onClick, personId, hasUser }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();

    const dateFrom = useDateTranslatorHelper(new Date(f?.dateFrom));
    const dateTo = useDateTranslatorHelper(f?.dateTo ? new Date(f?.dateTo) : null);

    const EventChip = ({ event }) => {
        const getChipColor = (state) => {
            switch (state) {
                case '0':
                    return 'success';
                case '1':
                    return 'warning';
                case '2':
                    return 'error';
                default:
                    return 'default';
            }
        };

        return (
            <Chip
                sx={{ mr: 1, mt: 0.5 }}
                label={
                    <Box component="span">
                        {event.description} - {event.message}
                    </Box>
                }
                color={getChipColor(event.state)}
                icon={<EventAvailable fontSize="small" />}
            />
        );
    };
    const IconCircle = ({ icon }) => {
        const theme = useTheme();
        return (
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'transparent',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1
                }}
            >
                {icon}
            </Box>
        );
    };

    return (
        <Paper
            elevation={3}
            sx={{
                mb: 2,
                p: 2.5,
                backgroundColor: dateTo ? '#EEEEEE' : ' #E8F5E9',
                borderRadius: 3,
                transition: '0.3s ease',
                '&:hover': {
                    boxShadow: 6
                },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',

                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
            }}
        >
            <Stack spacing={1}>
                {/* Title and Date */}
                <Stack direction="row" alignItems="center">
                    <IconCircle icon={<PersonOutlineOutlined fontSize="small" />} />
                    <Typography variant="h5" fontWeight={600} color="primary.dark">
                        {f?.function} ({dateFrom} - {dateTo || 'Σήμερα'})
                    </Typography>
                </Stack>

                {/* Org Info */}
                <Stack direction="row" alignItems="center">
                    <IconCircle icon={<LocationCityOutlined fontSize="small" />} />
                    <Typography variant="h5">
                        {f?.organization} ({f?.organizationAbbr} - {f?.organizationCode})
                    </Typography>
                </Stack>

                {/* Registration */}
                {f?.lblRegNumber && f?.regNumber && (
                    <Stack direction="row" alignItems="center">
                        <IconCircle icon={<BadgeOutlined fontSize="small" />} />
                        <Typography variant="h5">
                            {f?.lblCode1
                                ? `${f.lblRegNumber}: ${f.regNumber} - ${f.lblCode1}: ${f.code1}`
                                : `${f.lblRegNumber}: ${f.regNumber}`}
                        </Typography>
                    </Stack>
                )}

                {f?.personFunctionEvents?.map((event, index) => (
                    <Typography key={index} variant="h5" sx={{ color: 'text.secondary', ml: 3 }}>
                        <EventChip key={index} event={event} />
                    </Typography>
                ))}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignSelf: 'flex-end' }}>
                <Button variant="contained" onClick={() => onClick(f)}>
                    {t('View')}
                </Button>
                {!hasUser && f?.functionId > 200 && f?.isActive && (
                    <Button variant="contained" color="primary" onClick={() => navigate(`/users/create/${personId}/${f.personFunctionId}`)}>
                        {t('CreateNewUser')}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};
//

PersonFunction.propTypes = {
    f: PropTypes.object.isRequired,
    onClick: PropTypes.func.isRequired
};

export const PersonFunctionsPretty = ({ personId, showTitle = true, hasUser, functions }) => {
    // const [functions, setFunctions] = useState(null);

    const navigate = useNavigate();
    const { t } = useTranslation();

    // useEffect(() => {
    //     const getFunctions = async () => {
    //         const result = await person.getFunctions({ personId });
    //         setFunctions(result);
    //     };
    //     if (!isNil(personId)) getFunctions();
    //     else setFunctions(null);
    // }, [personId]);

    const onClick = (func) => {
        if (func.isOfficial) {
            navigate(`/officials/manage/${func.personFunctionId}/details`);
        } else if (func.dataKey === '@Athlete') {
            navigate(`/athletes/manage/${func.personFunctionId}/details`);
        }
    };

    return functions ? (
        <>
            {functions.map((f) => (
                <PersonFunction key={f.personFunctionId} f={f} onClick={onClick} personId={personId} hasUser={hasUser} />
            ))}
        </>
    ) : null;
};

export const Search = () => {
    const form = useForm();
    const { t } = useTranslation();
    const [personId, setPersonId] = useState(null);
    const [functions, setFunctions] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const personQuery = usePerson(personId);

    const handleSelect = (item) => {
        const selectedPersonId = item?.Id;
        setPersonId(selectedPersonId);
    };

    useEffect(() => {
        const getFunctions = async () => {
            const result = await person.getFunctions({ personId });
            setFunctions(result);
            if (result.length > 0 && result.filter((f) => f.statusId == 300).length > 0) {
                setHasAccess(true);
            } else {
                setHasAccess(false);
            }
        };
        if (!isNil(personId)) getFunctions();
        else {
            setFunctions(null);
            setHasAccess(false);
        }
    }, [personId]);

    return (
        <div style={{ marginTop: 18 }}>
            <FormProvider {...form}>
                <BringValueInput
                    id="Id"
                    name="Id"
                    placeholder={t('Search')}
                    dataKey="People-BV"
                    onNotFound={() => form.setValue('Id', null)}
                    onSelect={handleSelect}
                />
            </FormProvider>
            {personQuery.isSuccess && personQuery.data.id && <PersonInfo person={personQuery.data} hasAccess={hasAccess} />}
            <PersonFunctionsPretty personId={personId} hasUser={personQuery?.data?.hasUser} functions={functions} />
        </div>
    );
};

export default Search;
